
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isOwnerRole } from '../../config/menuCodeMap';
import { staffService, menusService } from '../../services';
import { ArrowLeft, Check, Mail } from 'lucide-react';
import {
  Loader, Select, Button, EmploymentHistory, useEmailAvailability,
  WeekPatternGrid, StaffAccessPicker, defaultWeekPattern, normalizeWeekPattern, weeklyPaidHours,
} from '../../components';
import dummyStaffData from '../../data/dummyStaff.json';
import { JOB_TITLES } from '../../utils/jobTitles';
import { toast } from 'react-hot-toast';

/**
 * The named sections of Step 1 — Basic Details. Completed in order, one per
 * sub-step, mirroring ServPatch-add-staff-prototype.html.
 */
const BASIC_SECTIONS = [
  { key: 'name', label: 'Name & account' },
  { key: 'identity', label: 'Personal & identity' },
  { key: 'address', label: 'Address' },
  { key: 'emergency', label: 'Emergency contact' },
  { key: 'bank', label: 'Bank details' },
  { key: 'employment', label: 'Employment details' },
  { key: 'checks', label: 'Checks & compliance' },
  { key: 'documents', label: 'Documents' },
];

const CONTRACT_TYPES = [
  { value: 'Permanent', label: 'Permanent' },
  { value: 'Fixed-term', label: 'Fixed-term (temporary)' },
  { value: 'Apprenticeship', label: 'Apprenticeship' },
  { value: 'Secondment', label: 'Secondment' },
];

const CHECK_STATUSES = ['Valid', 'Pending evidence', 'Expired'];

/**
 * How often to warn before a check runs out.
 *
 * The same four options, with the same STORED VALUES, as Staff Compliance and
 * the Recruitment check dialog (REMINDER_SCHEDULE_OPTIONS in
 * api/utils/recruitment.js). All three write the same two columns —
 * rtw_reminder and dbs_reminder — and complianceSync copies the value straight
 * across to compliance_checks.reminder_schedule, so a schedule set on one
 * screen has to be a schedule the other two recognise.
 *
 * This form used to store the LABEL ('90, 60, 30 and 7 days'), which arrived in
 * Staff Compliance as a value that screen has no option for. LEGACY_REMINDERS
 * below maps those older records onto the shared value as they are loaded, so
 * they correct themselves on the next save.
 */
const REMINDER_SCHEDULES = [
  { value: '90,60,30,7', label: '90, 60, 30 and 7 days' },
  { value: '60,30,7', label: '60, 30 and 7 days' },
  { value: '30,7', label: '30 and 7 days' },
  { value: '7', label: '7 days only' },
  { value: '', label: 'No reminder' },
];

const LEGACY_REMINDERS = {
  '90, 60, 30 and 7 days': '90,60,30,7',
  '60, 30 and 7 days': '60,30,7',
  '30 and 7 days': '30,7',
  '7 days only': '7',
  'No reminder': '',
};

/** A stored schedule, as one of the shared values. */
const reminderValue = (stored) => {
  if (stored === undefined || stored === null || stored === '') return '';
  const text = String(stored).trim();
  if (REMINDER_SCHEDULES.some((option) => option.value === text)) return text;
  return LEGACY_REMINDERS[text] ?? text;
};

// The same document types a candidate is asked for in Recruitment
// (DOCUMENT_DEFINITIONS in api/utils/recruitment.js). A staff member added here
// and one converted from a candidate then hold the same kinds of document, so
// the two records read alike.
const DOCUMENT_CATEGORIES = [
  'Right to Work evidence', 'Proof of identity', 'Proof of address',
  'CV / employment history', 'Qualifications / certificates',
  'DBS certificate / result',
];

// Shared input styling, so a field added later cannot drift from the rest.
const FIELD_CLASS = 'w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px]';
const LABEL_CLASS = 'block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5';

/* The weekly work pattern and the access picker are shared with the
 * Recruitment transfer — see components/WeekPatternGrid.js and
 * components/StaffAccessPicker.js. Both write the same fields either way, so a
 * transferred candidate and a staff member typed in here end up identical. */

const PAY_FREQUENCIES = ['Monthly', 'Four-weekly', 'Weekly'];

/**
 * Create/Edit Staff Page
 * Multi-Step Form for creating and editing staff members
 * Supports menu assignment
 */
const CreateEditStaff = () => {
  const navigate = useNavigate();
  const { user, sites, activeSiteId } = useAuth();
  // The sites ticked for a manager — just the site being worked in until the
  // owner ticks others.
  const managerSiteIdsOf = (form) => (form?.site_ids?.length ? form.site_ids : [activeSiteId].filter(Boolean));
  const { staffId } = useParams();
  const location = useLocation();
  const isEdit = !!staffId;
  const isApprovalMode = location.pathname.includes('/approve');
  // An existing profile (edit, or completing an approval) already has every
  // Basic details section filled in, so the chips open them in any order. Adding
  // a brand-new member still walks forward through Next, which is where the
  // per-section required fields are checked.
  const canJumpSections = isEdit || isApprovalMode;

  // Multi-Step State
  const [currentStep, setCurrentStep] = useState(1);
  const [subStep, setSubStep] = useState(1);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Whether anything on screen differs from what was last written.
   *
   * Compared against a snapshot rather than flagged at each setFormData call:
   * there are ~20 of them across eight sections, and the twenty-first would be
   * the one that forgets. The baseline is taken when the record finishes
   * loading and re-taken after every successful save.
   */
  const [dirty, setDirty] = useState(false);
  const savedSnapshot = useRef(null);
  const latestFormData = useRef(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);

  // What the server generated for a newly created staff member: their login id
  // and whether the set-PIN email actually went out. Drives the success banner,
  // which is the only place the manager sees the id.
  const [created, setCreated] = useState(null);

  // Documents already saved against this staff member, and the ones the
  // manager has picked but cannot upload yet. On a new staff member there is no
  // id to post a file to until the record exists, so files wait here and go up
  // straight after the create — see flushPendingDocuments().
  const [staffDocuments, setStaffDocuments] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  // The Documents section's own little compose row, cleared after each add.
  const [documentDraft, setDocumentDraft] = useState({ category: DOCUMENT_CATEGORIES[0], name: '', expiry: '', file: null });

  const [formData, setFormData] = useState({
    name: '',
    // The name is captured in three parts, exactly as the prototype does, but
    // the database stores one `users.name`. These are joined on save and split
    // back on edit; nothing downstream has to change.
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    menu_ids: [],
    // Detailed User Info
    userDetails: {
      preferred_name: '',
      dob: '',
      ni_number: '',
      current_address: '',
      previous_addresses: [],
      // Structured address. current_address is still written on save as the
      // composed one-line version, because the rest of the app reads that.
      address_line1: '',
      address_line2: '',
      address_town: '',
      address_postcode: '',
      address_country: 'United Kingdom',
      address_under_3_years: false,
      emergency_contact_name: '',
      emergency_contact_rel: '',
      emergency_contact_phone: '',
      next_of_kin: { name: '', relationship: '', phone: '' },
      // UI only, stripped before submit: when ticked the emergency contact is
      // copied into next_of_kin on save rather than typed twice.
      kin_same_as_emergency: true,
      nationality: '',
      immigration_status: '',
      visa_type: '',
      visa_expiry: '',
      share_code: '',
      photo_url: '',
      gender: '',
      ethnicity: '',
      disability_status: '',
      job_title: '',
      employment_type: 'Full-time',
      contract_type: 'Permanent',
      // Only meaningful on a fixed-term contract; cleared when the contract
      // type moves away from Fixed-term so a stale end date cannot linger.
      contract_end_date: '',
      start_date: '',
      probation_end_date: '',
      line_manager: '',
      assigned_locations: [],
      shift_pattern_details: '',
      pay_rate: '',
      // Payroll labels set on the Work roster step alongside the hourly rate.
      pay_frequency: 'Monthly',
      payroll_reference: '',
      // Numeric rate used by payroll. Empty means "not set", which payroll
      // reports as hours without a pay figure rather than as unpaid work.
      hourly_rate: '',
      agency_name: '',
      // Payroll bank details. The candidate gives these on their onboarding
      // form (see ONBOARDING_EDITABLE_DETAIL_FIELDS in api/utils/recruitment.js);
      // a manually added starter has no such form, so they are asked here.
      bank_account_name: '',
      bank_sort_code: '',
      bank_account_number: '',
      bank_building_society_ref: '',
      previous_employers: [],
      notice_period: '',
      dbs_cert_number: '',
      dbs_issue_date: '',
      dbs_level: 'Enhanced with Barred List',
      dbs_update_service_subs: false,
      dbs_update_check_date: '',
      dbs_status: '',
      dbs_reminder: '90,60,30,7',
      dbs_notes: '',
      rtw_doc_type: '',
      rtw_doc_number: '',
      rtw_expiry_date: '',
      rtw_verification_date: '',
      rtw_verified_by: '',
      rtw_status: '',
      rtw_reminder: '90,60,30,7',
      rtw_notes: '',
      id_doc_type: '',
      id_doc_number: '',
      id_verification_date: '',
      address_doc_type: '',
      address_verification_date: '',
      health_submitted_date: '',
      health_clearance_date: '',
      health_fit_for_role: true,
      health_restrictions: '',
      professional_reg_name: '',
      professional_reg_number: '',
      professional_reg_expiry: '',
      professional_reg_verified_date: '',
      criminal_convictions_disclosure: false,
      employment_gap_check: '',
      overseas_police_check: false,
      interview_date: '',
      interview_panel: [],
      interview_outcome: '',
      interview_score: '',
      certificates: [],
      // Specialized Domiciliary/Carer Fields
      max_visits_per_day: '',
      dbs_expiry: '',
      skills: [],
      // Text helpers for JSON fields
      previous_addresses_text: '',
      next_of_kin_text: '',

      references_text: ''
    },
    // The Carer toggle on the Module Access step. On -> they use the mobile
    // app; the API derives the role id from it (carer role for the org's type,
    // otherwise the dashboard-only staff role). Dashboard access is the
    // separate axis: menu_ids. Either or both, never neither.
    is_carer: false,
    // Owner-only Manager switch on the Access step. On -> the API gives them
    // the org's manager role, its modules and the app; nothing else to pick.
    is_manager: false,
    // Multi-site groups: the sites a manager runs (empty = just this site).
    site_ids: [],
    // Mobile app sign-in, the checkbox under the Carer toggle. Forced on (and
    // shown locked) while Carer is on, because a carer always uses the app.
    // With Carer off it is how a normal staff member gets an app login WITHOUT
    // being an employee — and in the app they are view-only: every screen, no
    // clocking in and no posting to a care record.
    app_access: false,
    // "Can log medication" — carers only, off until the manager ticks it.
    // Without it the carer sees medication in the app but cannot record a dose.
    medication_access: false,
    // Roster Fields
    group_id: '',
    /* The weekly pattern, one row per day.
     *
     * A care home reads these as shifts — Working / Start / Finish / Unpaid
     * break, with the paid hours following from them. A domiciliary service
     * reads the same rows as AVAILABILITY — Available / from / until, no break
     * and no paid hours, because the visits themselves are assigned later in
     * Rota. Which reading applies comes from the organisation's own type, not
     * from a switch on this screen.
     *
     * The API derives working_days / shift_start / shift_end from this on every
     * save, so the rota, clock-in and payroll callers are untouched. See
     * api/utils/weekPattern.js. */
    week_pattern: defaultWeekPattern(),
    contracted_hours: '',
    shift_name: 'Day'
  });
  
  // Checked as the manager finishes typing. On this form especially: without it
  // "already in use" is not heard until Save, three steps and six sections
  // later, and the offending box is nowhere on screen by then. While editing,
  // their own address is still theirs — hence exclude_id.
  const emailCheck = useEmailAvailability(formData.email, { excludeId: isEdit ? staffId : null });

  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const [groups, setGroups] = useState([]);
  // A domiciliary service records availability; a care home rosters shifts.
  // Taken from the organisation, the same axis the rest of the dashboard
  // already branches on (the Residents→Clients rename, care-home-only menus).
  const isDomiciliaryOrg =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  /* ── The weekly pattern grid ──────────────────────────────────────────── */

  const workingDayCount = formData.week_pattern.filter((e) => e.on).length;
  const paidHoursThisWeek = isDomiciliaryOrg
    ? 0
    : weeklyPaidHours(formData.week_pattern);
  const contractedHoursNumber = Number(formData.contracted_hours);
  const hasContractedHours = formData.contracted_hours !== '' && Number.isFinite(contractedHoursNumber);
  const hoursDifference = hasContractedHours
    ? Math.round((paidHoursThisWeek - contractedHoursNumber) * 100) / 100
    : null;
  
  const fillTestData = () => {
    const randomId = Math.floor(1000 + Math.random() * 9000);
    setFormData(prev => ({
      ...prev,
      ...dummyStaffData,
      // The form edits the name in three parts, so the test data has to fill
      // those rather than the composed `name` it used to set.
      first_name: String(dummyStaffData.name || '').trim().split(/\s+/)[0] || 'Alex',
      middle_name: '',
      last_name: `${String(dummyStaffData.name || '').trim().split(/\s+/).slice(1).join(' ') || 'Taylor'} ${randomId}`,
      name: `${dummyStaffData.name} ${randomId}`,
      email: `alex.${randomId}@servpatch.care`,
      is_carer: true,
      app_access: true,
      userDetails: {
        ...prev.userDetails,
        ...dummyStaffData.userDetails,
        start_date: new Date().toISOString().split('T')[0]
      }
    }));
  };

  // Keep the newest form state reachable from async handlers, which close over
  // the formData of the render they were created in.
  latestFormData.current = formData;

  // First snapshot once the record is on screen — on an edit that is after the
  // fetch replaces the empty initial state, which would otherwise read as an
  // edit the manager never made.
  useEffect(() => {
    if (loading) return;
    const now = JSON.stringify(formData);
    if (savedSnapshot.current === null) {
      savedSnapshot.current = now;
      return;
    }
    setDirty(savedSnapshot.current !== now);
  }, [formData, loading]);

  /** Everything on screen is now what is stored. */
  const markSaved = () => {
    savedSnapshot.current = JSON.stringify(latestFormData.current);
    setDirty(false);
  };

  /**
   * Load available menus on component mount
   */
  useEffect(() => {
    loadAvailableMenus();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await staffService.getStaffGroups();
      if (response.success) {
        setGroups(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    try {
      const response = await staffService.createStaffGroup({ name: newGroupName });
      if (response.success) {
        toast.success('Group created successfully');
        setNewGroupName('');
        setIsCreatingGroup(false);
        fetchGroups();
        setFormData(prev => ({ ...prev, group_id: String(response.data.id) }));
      }
    } catch (err) {
      toast.error(err.message || 'Error creating group');
    }
  };

  /**
   * Load staff data if editing
   */
  useEffect(() => {
    if (isEdit) {
      loadStaff();
    }
  }, [staffId]);

  /**
   * Fetch all available menus (non-admin menus for staff)
   */
  const loadAvailableMenus = async () => {
    try {
      setLoadingMenus(true);
      const response = await menusService.getUserMenus();

      if (response.success) {
        // Get allMenus from nested response structure
        const allMenus = response.data.allMenus || [];

        // Filter only non-admin menus for staff
        const staffMenus = allMenus.filter(menu => !menu.is_admin);
        setAvailableMenus(staffMenus);
      } else {
        setError('Failed to load menus');
      }
    } catch (err) {
      console.error('Error loading menus:', err);
      setError(err.message || 'Error loading menus');
    } finally {
      setLoadingMenus(false);
    }
  };


  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await staffService.getStaffById(staffId);

      if (response.success) {
        // users.name is one string; the form edits it as three parts. First
        // word is the forename, last is the surname, anything between is the
        // middle name — the same split the prototype uses.
        const nameParts = String(response.data.name || '').trim().split(/\s+/).filter(Boolean);
        const details = response.data.userDetails || {};

        setStaffDocuments(Array.isArray(details.staff_documents) ? details.staff_documents : []);

        setFormData({
          name: response.data.name,
          first_name: nameParts[0] || '',
          middle_name: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
          last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          menu_ids: response.data.menu_ids || [],
          // Roster Details
          group_id: response.data.rosterDetails?.group_id ? String(response.data.rosterDetails.group_id) : '',
          // The API always sends a per-day pattern — building one from the old
          // working_days / shift_start / shift_end columns for anybody saved
          // before per-day existed — so this never has to guess. See
          // withWeekPattern in the staff controller.
          week_pattern: normalizeWeekPattern(response.data.rosterDetails?.week_pattern, {
            domiciliary: String(response.data.rosterDetails?.pattern_mode || '').toUpperCase() === 'DOMICILIARY',
          }),
          contracted_hours: response.data.rosterDetails?.contracted_hours ?? '',
          shift_name: response.data.rosterDetails?.shift_name || 'Day',
          // Prefills the Carer toggle. The API derives it from the role id, so
          // staff created before the toggle existed read back correctly; the
          // role-id fallback covers an older API that doesn't send the flag.
          is_carer: response.data.is_carer !== undefined
            ? response.data.is_carer === true
            : [3, 5].includes(Number(response.data.role_id)),
          // A carer always has the app, so an older API that doesn't send the
          // flag still reads back correctly for them.
          app_access: response.data.app_access !== undefined
            ? response.data.app_access === true
            : [3, 5].includes(Number(response.data.role_id)),
          medication_access: response.data.medication_access === true,
          is_manager: response.data.is_manager === true,
          site_ids: Array.isArray(response.data.site_ids) ? response.data.site_ids.map(Number) : [],
          userDetails: {
            ...formData.userDetails,
            ...response.data.userDetails,
            // Null from the database would make this a controlled input with a
            // null value, which React treats as uncontrolled and then warns
            // about the moment it's typed into. Empty string is "not set".
            hourly_rate: response.data.userDetails?.hourly_rate ?? '',
            // Same reason as hourly_rate — null would make these uncontrolled.
            pay_frequency: response.data.userDetails?.pay_frequency ?? 'Monthly',
            payroll_reference: response.data.userDetails?.payroll_reference ?? '',
            bank_account_name: response.data.userDetails?.bank_account_name ?? '',
            bank_sort_code: response.data.userDetails?.bank_sort_code ?? '',
            bank_account_number: response.data.userDetails?.bank_account_number ?? '',
            bank_building_society_ref: details.bank_building_society_ref ?? '',
            // Structured address. A record saved before these columns existed
            // has only the one-line current_address; line 1 is seeded from it
            // so nothing looks lost, and re-saving fills the parts in properly.
            address_line1: details.address_line1
              || (details.address_town || details.address_postcode ? '' : (details.current_address || '')),
            address_line2: details.address_line2 ?? '',
            address_town: details.address_town ?? '',
            address_postcode: details.address_postcode ?? '',
            address_country: details.address_country || 'United Kingdom',
            // A record saved before this column existed has the flag off but a
            // previous address recorded. Reading that as "under 3 years" keeps
            // the history visible instead of quietly dropping it on the next save.
            address_under_3_years: !!details.address_under_3_years
              || (Array.isArray(details.previous_addresses) && details.previous_addresses.length > 0),
            contract_end_date: details.contract_end_date ?? '',
            rtw_status: details.rtw_status ?? '',
            rtw_reminder: reminderValue(details.rtw_reminder ?? REMINDER_SCHEDULES[0].value),
            rtw_notes: details.rtw_notes ?? '',
            dbs_status: details.dbs_status ?? '',
            dbs_reminder: reminderValue(details.dbs_reminder ?? REMINDER_SCHEDULES[0].value),
            dbs_notes: details.dbs_notes ?? '',
            // Next of kin is stored as JSON but older rows hold a plain string.
            next_of_kin: typeof details.next_of_kin === 'object' && details.next_of_kin !== null
              ? { name: '', relationship: '', phone: '', ...details.next_of_kin }
              : { name: details.next_of_kin || '', relationship: '', phone: '' },
            // Only default the "same as emergency contact" tick on when there
            // is genuinely no separate next of kin recorded.
            kin_same_as_emergency: !(details.next_of_kin && (typeof details.next_of_kin === 'string' || details.next_of_kin.name)),
            // Restore text helpers from JSON fields for editing
            previous_addresses_text: Array.isArray(response.data.userDetails?.previous_addresses)
              ? response.data.userDetails.previous_addresses.join('\n')
              : response.data.userDetails?.previous_addresses || '',
            next_of_kin_text: typeof response.data.userDetails?.next_of_kin === 'string'
              ? response.data.userDetails.next_of_kin
              : response.data.userDetails?.next_of_kin?.name
                ? `${response.data.userDetails.next_of_kin.name}${response.data.userDetails.next_of_kin.relationship ? ', ' + response.data.userDetails.next_of_kin.relationship : ''}${response.data.userDetails.next_of_kin.phone ? ', ' + response.data.userDetails.next_of_kin.phone : ''}`
                : '',
            // The API normalises this to a list of entry objects — including
            // rows that still hold the old plain list of employer names — so
            // this only has to guard against a missing value.
            previous_employers: Array.isArray(response.data.userDetails?.previous_employers)
              ? response.data.userDetails.previous_employers
              : [],
            references_text: ''
          }
        });
      } else {
        setError(response.message || 'Failed to load staff');
      }
    } catch (err) {
      setError(err.message || 'Error loading staff');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Handle nested userDetails change
   */
  const handleDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        [name]: val
      }
    }));
  };

  /**
   * The account number is typed twice, so a transposed digit is caught here
   * rather than by a failed payment. Blank counts as matching — the whole
   * section is optional and can be left for the staff member.
   */
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const accountNumber = formData.userDetails.bank_account_number || '';
  const accountNumbersMatch = !accountNumber || !confirmAccountNumber || accountNumber === confirmAccountNumber;

  /**
   * Sort codes are stored and shown as 00-00-00. Typing is digits-only and the
   * dashes are inserted as you go, so a manager cannot save "112233" one time
   * and "11-22-33" the next.
   */
  const handleSortCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    const formatted = digits.replace(/(\d{2})(?=\d)/g, '$1-');
    setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, bank_sort_code: formatted } }));
  };

  /**
   * Contract end date only applies to a fixed-term contract. Moving away from
   * Fixed-term clears it, so a stale end date cannot be saved against a
   * permanent contract.
   */
  const handleContractTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        contract_type: value,
        contract_end_date: value === 'Fixed-term' ? prev.userDetails.contract_end_date : '',
      },
    }));
  };

  const handleNextOfKinChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        next_of_kin: { ...(prev.userDetails.next_of_kin || {}), [field]: value },
      },
    }));
  };

  /**
   * Documents & check evidence.
   *
   * On an existing staff member a picked file goes straight up. On a new one
   * there is no id to post to yet, so it is queued and flushed by
   * flushPendingDocuments() the moment the record has been created.
   */
  const queueOrUploadDocument = async (document) => {
    if (!document.file) {
      toast.error('Choose a file to upload first.');
      return;
    }

    if (!isEdit) {
      setPendingDocuments(prev => [...prev, document]);
      toast.success(`${document.name || document.file.name} will be uploaded when you save.`);
      return;
    }

    try {
      setUploadingDocument(true);
      const response = await staffService.addStaffDocument(staffId, document);
      if (response.success) {
        setStaffDocuments(response.documents || []);
        toast.success('Document added');
      } else {
        toast.error(response.message || 'Could not add the document');
      }
    } catch (err) {
      toast.error(err.message || 'Could not add the document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const removeSavedDocument = async (documentId) => {
    try {
      const response = await staffService.deleteStaffDocument(staffId, documentId);
      if (response.success) {
        setStaffDocuments(response.documents || []);
        toast.success('Document removed');
      } else {
        toast.error(response.message || 'Could not remove the document');
      }
    } catch (err) {
      toast.error(err.message || 'Could not remove the document');
    }
  };

  /**
   * Upload everything queued while the staff member had no id yet. A failed
   * upload is reported but never fails the save — the staff record already
   * exists by this point and the manager can re-attach from the profile.
   */
  const flushPendingDocuments = async (newStaffId) => {
    if (!pendingDocuments.length) return;
    const failed = [];
    for (const document of pendingDocuments) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await staffService.addStaffDocument(newStaffId, document);
      } catch (err) {
        failed.push(document.name || document.file?.name || 'document');
      }
    }
    setPendingDocuments([]);
    if (failed.length) {
      toast.error(`Saved, but these files did not upload: ${failed.join(', ')}. Add them again from the staff profile.`, { duration: 8000 });
    }
  };

  const addDraftDocument = async () => {
    await queueOrUploadDocument({
      category: documentDraft.category,
      name: documentDraft.name || documentDraft.file?.name || documentDraft.category,
      expiry: documentDraft.expiry,
      file: documentDraft.file,
    });
    setDocumentDraft({ category: DOCUMENT_CATEGORIES[0], name: '', expiry: '', file: null });
  };

  /**
   * Toggle menu selection.
   *
   * Some modules have sub-menus (Staff → Recruitment, Staff Compliance, Leave &
   * Approval). Ticking the parent grants its sub-menus too, and unticking it
   * takes them away — see src/utils/menuTree.js for the map.
   */
  // Turning a day on or off lives in the shared WeekPatternGrid,
  // which also keeps that day's times and break.

  /**
   * What is wrong with the section on screen, if anything.
   *
   * Pulled out of handleNext so that "Save & continue" can run exactly the same
   * checks: a section that cannot be moved past must not be saveable past
   * either, and two copies of these rules would drift.
   *
   * @returns {string|null} the message to show, or null when it is fine
   */
  const validateCurrentStep = () => {
    if (currentStep === 1) {
      // Only the fields the form marks with an asterisk block progress. Every
      // other Basic details section can be left for the staff member to
      // complete later, which is how the sections are ordered.
      if (subStep === 1) {
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
          return 'Please enter both a first name and a surname.';
        }
        // The login id is generated and the PIN is set by the staff member
        // through an emailed link, so the email address is what the account
        // depends on.
        if (!isEdit && !formData.email.trim()) return 'We need an email address — it is where their link to set a login PIN is sent.';
        if (formData.email.trim() && !/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
          return 'That email address does not look right — check it before continuing.';
        }
        if (emailCheck.state === 'taken') return emailCheck.message;
      }

      if (subStep === 5 && !accountNumbersMatch) {
        return 'The two account numbers do not match. Check them before continuing.';
      }

      if (subStep === 6) {
        if (!formData.userDetails.job_title) return 'Please specify the Job Title.';
        if (!formData.userDetails.start_date) return 'When does this staff member start? Please select a date.';
      }
      return null;
    }

    if (currentStep === 2) {
      if (formData.week_pattern.every((entry) => !entry.on)) {
        return isDomiciliaryOrg
          ? 'Please mark at least one day they are available.'
          : 'Please select at least one day that they will be working.';
      }
    }

    return null;
  };

  /** Move to the next section or step. The movement only — no validation. */
  const advanceStep = () => {
    if (currentStep === 1 && subStep < BASIC_SECTIONS.length) { setSubStep(subStep + 1); return; }
    if (currentStep === 1) { setCurrentStep(2); setSubStep(1); return; }
    // Module Access is the last step for everybody: it is where the Carer
    // toggle lives, so a carer passes through it too (they may simply leave
    // every module unticked).
    if (currentStep === 2) { setCurrentStep(3); return; }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  /**
   * Step Navigation Handlers
   */
  const handleNext = () => {
    setError(null);
    const problem = validateCurrentStep();
    if (problem) { setError(problem); return; }
    advanceStep();
  };

  /**
   * "Save changes" — write the record and stay on this section.
   *
   * EDITING ONLY. Editing used to mean walking every section to the last step
   * before anything could be written: change a phone number in section 1 and
   * you still had to pass the roster and the access ticks to save it. Each
   * section now commits on its own.
   *
   * A new starter has no record to update yet, and creating one from section 1
   * would put a half-typed person in the directory with no roster and nowhere
   * to sign in — so they still walk to the end, which is where their login id
   * and set-PIN email are produced.
   */
  const handleSaveOnly = async () => {
    setError(null);
    const problem = validateCurrentStep();
    if (problem) { setError(problem); return; }
    await handleSubmit(null, { stay: true, advance: false });
  };

  /**
   * "Next" — move on, saving first if anything was typed.
   *
   * Next does not silently drop an edit: a section left dirty is written on the
   * way past, which is what the button did when it read "Save & Next". The
   * difference is that Save changes now exists for staying put.
   */
  const handleNextWhileEditing = async () => {
    setError(null);
    const problem = validateCurrentStep();
    if (problem) { setError(problem); return; }
    // handleSubmit advances on success, so a failed save leaves the manager on
    // the section that failed rather than a step past it.
    if (dirty) { await handleSubmit(null, { stay: true }); return; }
    advanceStep();
  };

  const handlePrev = () => {
    setError(null);

    if (currentStep === 1 && subStep > 1) {
      setSubStep(prev => prev - 1);
      return;
    }

    if (currentStep === 2) {
      setCurrentStep(1);
      setSubStep(BASIC_SECTIONS.length);
      return;
    }

    if (currentStep === 3) {
      setCurrentStep(2);
      return;
    }

    setCurrentStep(prev => prev - 1);
  };

  /**
   * Final Form Submission
   */
  /**
   * Save the form.
   *
   * `stay` writes the record and leaves the manager where they are, then moves
   * on one section — what "Save & continue" does while editing. It is the same
   * payload and the same endpoint either way: a record saved halfway through
   * the wizard is a complete record, not a draft, so there is nothing to
   * reconcile afterwards.
   */
  const handleSubmit = async (e, { stay = false, advance = true } = {}) => {
    e?.preventDefault();

    // Safety: If somehow handleSubmit is called before the final step (e.g. Enter key),
    // redirect to handleNext instead of submitting.
    if (!stay && currentStep < totalSteps) {
      handleNext();
      return;
    }

    // The three name inputs are the source of truth; users.name is composed
    // from them. Everything downstream (directory, rosters, emails) reads name.
    const fullName = [formData.first_name, formData.middle_name, formData.last_name]
      .map(part => String(part || '').trim())
      .filter(Boolean)
      .join(' ');

    // The two jumps below must not fire while staying: they would drag the
    // manager out of the section they are working on.
    if (!fullName) {
      setError('Please enter both a first name and a surname.');
      if (!stay) { setCurrentStep(1); setSubStep(1); }
      return;
    }

    if (!accountNumbersMatch) {
      setError('The two account numbers do not match. Check them before saving.');
      if (!stay) { setCurrentStep(1); setSubStep(5); }
      return;
    }

    // Explicit confirmation to prevent accidental/automatic saves. Not asked of
    // "Save & continue": pressing a button labelled Save IS the confirmation,
    // and a dialog on every section is the friction this replaces.
    if (!stay) {
      const confirmMsg = isEdit
        ? "Are you sure you want to save these changes?"
        : `Are you sure you want to create the profile for ${fullName}?`;

      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    setError(null);

    // A person has to be able to sign in somewhere. Carer on = the mobile app,
    // modules ticked = the dashboard, both = both. Neither is the only
    // combination that leaves them with no way in. Mirrors the same check in
    // the API's createStaff/updateStaff.
    if (!formData.is_manager && !formData.is_carer && !formData.app_access && formData.menu_ids.length === 0) {
      setError('This person has nowhere to sign in. Turn on "Carer", or tick "Mobile app access", or select at least one dashboard module.');
      if (!stay) setCurrentStep(3);
      return;
    }

    try {
      setSubmitting(true);
      let response;

      // Clean up userDetails before submission (remove UI-only text helpers)
      const {
        previous_addresses_text,
        next_of_kin_text,
        references_text,
        kin_same_as_emergency,
        // Documents are written by their own upload endpoint. Sending the copy
        // this form loaded would overwrite anything uploaded since — including
        // the files added a moment ago in the Documents section.
        staff_documents,
        // References are written by the candidate, the manager and the referee
        // — never here. Omitting the key leaves what is stored untouched; a
        // record loaded before this form stopped carrying them must not post
        // that stale copy back over a reference that has since come in.
        references_data,
        ...restUserDetails
      } = formData.userDetails;

      // The address is captured in parts but the staff profile, rosters and
      // exports all read current_address, so it is rebuilt on every save.
      const composedAddress = [
        restUserDetails.address_line1,
        restUserDetails.address_line2,
        restUserDetails.address_town,
        restUserDetails.address_postcode,
        restUserDetails.address_country,
      ].map(part => String(part || '').trim()).filter(Boolean).join(', ');

      // A next of kin who is the emergency contact is copied across rather than
      // typed twice, which is what the tickbox on the form means.
      const nextOfKin = kin_same_as_emergency
        ? {
          name: restUserDetails.emergency_contact_name || '',
          relationship: restUserDetails.emergency_contact_rel || '',
          phone: restUserDetails.emergency_contact_phone || '',
        }
        : (restUserDetails.next_of_kin || { name: '', relationship: '', phone: '' });

      // Recursive cleaner for "Invalid date" and empty strings
      const sanitizeData = (data) => {
        if (!data || typeof data !== 'object') return data;
        const newObj = Array.isArray(data) ? [] : {};
        for (const [key, val] of Object.entries(data)) {
          // MySQL DATEONLY columns reject empty strings; convert to null
          if (val === 'Invalid date' || val === '') newObj[key] = null;
          else if (typeof val === 'object' && val !== null) newObj[key] = sanitizeData(val);
          else newObj[key] = val;
        }
        return newObj;
      };

      // Map UI text helpers back to the model's JSON fields
      const cleanUserDetails = sanitizeData({
        ...restUserDetails,
        current_address: composedAddress,
        // Address history is only asked for, and only kept, while the "less
        // than 3 years" tick is on — otherwise unticking it would leave a
        // previous address saved against a field the form no longer shows.
        previous_addresses: restUserDetails.address_under_3_years && previous_addresses_text
          ? previous_addresses_text.split('\n').filter(a => a.trim() !== '')
          : [],
        next_of_kin: nextOfKin.name || nextOfKin.relationship || nextOfKin.phone ? nextOfKin : null,
        // Employment history, as entered in the Employment history section.
        // The API cleans and orders it (utils/employmentHistory.js).
        previous_employers: restUserDetails.previous_employers || [],
        // A free-text note about references, if anything was typed. The
        // referees themselves are never written from here — see above.
        ...(references_text && { references_text })
      });

      if (isEdit) {
        const updateData = {
          name: fullName,
          email: formData.email,
          phone: formData.phone,
          is_carer: formData.is_carer,
          app_access: formData.is_carer || formData.app_access,
          medication_access: formData.is_carer && formData.medication_access,
          menu_ids: formData.menu_ids,
          // Only an owner can change it; anyone else sends nothing and it stands.
          ...(isOwnerRole(user) && {
            is_manager: formData.is_manager,
            ...(formData.is_manager && { site_ids: managerSiteIdsOf(formData) }),
          }),
          userDetails: cleanUserDetails,
          // If in approval mode, also send the status update
          ...(isApprovalMode && { approval_status: 'approved' })
        };
        response = await staffService.updateStaff(staffId, updateData);
      } else {
        const createData = {
          ...formData,
          name: fullName,
          is_manager: isOwnerRole(user) && formData.is_manager,
          ...(isOwnerRole(user) && formData.is_manager && { site_ids: managerSiteIdsOf(formData) }),
          userDetails: cleanUserDetails
        };
        response = await staffService.createStaff(createData);
      }

      if (response.success) {
        const savedStaffId = isEdit ? staffId : response.data.id;

        // Files picked before the record existed can only go up now that it has
        // an id. Never fails the save — see flushPendingDocuments().
        await flushPendingDocuments(savedStaffId);

        await staffService.upsertStaffRoster(savedStaffId, {
          // The pattern is what is sent; the API derives working_days,
          // shift_start and shift_end from it so the rota, clock-in and payroll
          // callers keep reading the shape they always did.
          week_pattern: formData.week_pattern,
          pattern_mode: isDomiciliaryOrg ? 'DOMICILIARY' : 'CARE_HOME',
          contracted_hours: formData.contracted_hours === '' ? null : formData.contracted_hours,
          shift_name: formData.shift_name,
          group_id: formData.group_id
        });

        // Saved and staying: confirm it and either move on or hold still,
        // rather than showing the finished-and-leaving screen. The toast is the
        // only acknowledgement when nothing moves, so it says what happened.
        if (stay) {
          markSaved();
          toast.success('Changes saved');
          setSubmitting(false);
          if (advance) advanceStep();
          return;
        }

        setSuccess(true);

        if (!isEdit) {
          // The generated user ID and the set-PIN email are the whole point of
          // this screen's last step — hold the page long enough to read them.
          setCreated({
            user_id: response.data?.user_id,
            email: response.data?.email || formData.email,
            emailed: response.data?.emailed !== false,
          });
          if (response.data?.emailed === false) {
            toast.error(response.message || 'Staff created, but the set-PIN email could not be sent.', { duration: 8000 });
          } else {
            toast.success(response.message || 'Staff created — a set-PIN link has been emailed.', { duration: 6000 });
          }
        }

        setTimeout(() => {
          navigate('/manager/staffs');
        }, isEdit ? 1500 : 5000);
      } else {
        setError(response.message || `Failed to ${isEdit ? 'update' : 'create'} staff`);
      }
    } catch (err) {
      setError(err.message || 'Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader text="Loading staff profile..." />;
  }

  const steps = [
    { id: 1, label: 'Basic Details', desc: 'Primary info' },
    { id: 2, label: 'Work Roster', desc: 'Schedule & shifts' },
    // Shown for everybody: this is where the Carer toggle (mobile app access)
    // and the module ticks (dashboard access) are set, and every staff member
    // needs at least one of the two.
    { id: 3, label: 'Access', desc: 'App & dashboard' },
  ];

  const totalSteps = steps.length;

  return (
    <div className="w-full space-y-6 pb-12 font-sans">

      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={() => navigate('/manager/staffs')}
          className="p-2 hover:bg-white dark:hover:bg-[#31373f] rounded-xl transition shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        </button>
        <div>
          <h1 className="text-[24px] font-semibold text-[#1E293B] dark:text-gray-100 tracking-tight">
            {isApprovalMode ? 'Complete & Approve Profile' : (isEdit ? 'Edit Staff Member' : 'Add Staff Member')}
          </h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-0.5">
            {isApprovalMode
              ? 'Complete the compliance details and roster to approve this employee'
              : (isEdit
                ? 'Update staff member information and permissions'
                : 'Create a new staff member account and set permissions')}
          </p>
        </div>
        
        {/* Test Fill Button (Conditional) */}
        {import.meta.env.VITE_SHOW_TEST_BUTTON === 'true' && !isEdit && (
          <button
            onClick={fillTestData}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 transition font-semibold text-[13px]"
          >
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            Fill Test Data
          </button>
        )}
      </div>

      <div className="flex flex-col xl:flex-row gap-6 items-start w-full">

        {/* Main Wizard Area */}
        <div className="flex-1 w-full">

          <div className="bg-white dark:bg-[#262b33] rounded-[16px] shadow-sm border border-[#E2E8F0] dark:border-gray-800 p-6 lg:p-8">

            {/* Stepper Header */}
            <div className="flex items-center justify-between mb-8 pb-8 border-b border-[#E2E8F0] dark:border-gray-800">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className={`flex items-center gap-3 ${currentStep !== step.id ? 'opacity-70' : 'opacity-100'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-[14px] transition-all duration-300 ${currentStep > step.id ? 'bg-[#2563EB] text-white' :
                      currentStep === step.id ? 'bg-[#2563EB] text-white ring-4 ring-[#EFF6FF]' :
                        'bg-[#F1F5F9] dark:bg-[#31373f] text-[#94A3B8] dark:text-gray-500'
                      }`}>
                      {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                    </div>
                    <div className="hidden sm:block">
                      <p className={`text-[14px] font-semibold ${currentStep >= step.id ? 'text-[#1E293B] dark:text-gray-200' : 'text-[#94A3B8] dark:text-gray-500'}`}>
                        {step.label}
                      </p>
                      <p className="text-[12px] text-[#64748B] dark:text-gray-400">{step.desc}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-1 mx-4 sm:mx-8 rounded-full bg-[#F1F5F9] dark:bg-[#31373f] overflow-hidden">
                      <div
                        className="h-full bg-[#2563EB] transition-all duration-500 ease-in-out"
                        style={{ width: currentStep > step.id ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Alerts */}
            {success && (
              <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/50 text-emerald-800 dark:text-emerald-400 px-5 py-4 rounded-2xl text-[14px] font-semibold flex items-center gap-3 animate-in fade-in zoom-in duration-300">
                <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-[15px]">Successfully Saved!</p>
                  <p className="text-emerald-600 font-normal">Details for <strong>{[formData.first_name, formData.last_name].filter(Boolean).join(" ")}</strong> have been {isEdit ? 'updated' : 'recorded'}.</p>

                  {/* The generated login id and the set-PIN email: this banner is
                      the only place the manager is shown either of them. */}
                  {created && (
                    <div className="mt-3 space-y-2 font-normal">
                      <p className="text-emerald-700 dark:text-emerald-400">
                        User ID: <strong className="tracking-wide">{created.user_id}</strong> (generated automatically)
                      </p>
                      {created.emailed ? (
                        <p className="text-emerald-600">
                          A link to set their login PIN has been emailed to <strong>{created.email}</strong>.
                          Please ask them to check their email and set their PIN — they can't sign in until they do.
                        </p>
                      ) : (
                        <p className="text-rose-600 dark:text-rose-400">
                          The set-PIN email could <strong>not</strong> be sent to {created.email}. Check the address
                          and resend the link from their staff profile.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 bg-rose-50 dark:bg-red-900/10 border border-rose-100 dark:border-red-900/50 text-rose-800 dark:text-red-400 px-5 py-4 rounded-2xl text-[14px] flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="w-6 h-6 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 mt-0.5 shrink-0">
                  <span className="font-bold text-[14px]">!</span>
                </div>
                <div>
                  <p className="font-bold">Wait a moment...</p>
                  <p className="text-rose-600/90 font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Form Content Wrapper */}
            <form
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                  e.preventDefault();
                }
              }}
              className="min-h-[280px]"
            >

              {/* STEP 1: Basic Details — eight named sections, completed in
                  order. Field-for-field the same as the Basic details step of
                  ServPatch-add-staff-prototype.html. */}
              {currentStep === 1 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-300">

                  {/* Section rail. When adding someone new a manager can only jump
                      back to a section already passed — going forward runs through
                      Next so the required fields are checked on the way. When the
                      profile already exists (edit or approval) every section can be
                      opened straight from the chips, in either direction. */}
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 mb-2">
                    Basic details — {canJumpSections ? 'pick any section to edit' : 'complete each named section in order'}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {BASIC_SECTIONS.map((section, index) => {
                      const number = index + 1;
                      const isCurrent = subStep === number;
                      const isDone = subStep > number;
                      return (
                        <button
                          key={section.key}
                          type="button"
                          onClick={() => {
                            if (canJumpSections || number < subStep) { setError(null); setSubStep(number); }
                          }}
                          disabled={!canJumpSections && number > subStep}
                          className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${isCurrent
                            ? 'bg-[#2563EB] text-white border-[#2563EB] shadow-sm'
                            : isDone
                              ? 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50 hover:border-emerald-400'
                              : `bg-white dark:bg-[#31373f] text-[#94A3B8] dark:text-gray-500 border-gray-200 dark:border-gray-700 ${canJumpSections ? 'hover:border-[#2563EB] hover:text-[#2563EB]' : 'cursor-not-allowed'}`
                            }`}
                        >
                          {number}. {section.label}
                          {isDone && <Check className="w-3 h-3 inline ml-1.5" strokeWidth={3} />}
                        </button>
                      );
                    })}
                  </div>

                  {/* 1. Name & account */}
                  {subStep === 1 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 1 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Name &amp; account</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Enter the legal name exactly as shown on the person's identity document.</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                        <div>
                          <label className={LABEL_CLASS}>First name <span className="text-red-500">*</span></label>
                          <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First name" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Middle name</label>
                          <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} placeholder="Optional" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Surname <span className="text-red-500">*</span></label>
                          <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Surname" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Preferred name</label>
                          <input type="text" name="preferred_name" value={formData.userDetails.preferred_name} onChange={handleDetailsChange} placeholder="Name used at work" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Email address {!isEdit && <span className="text-red-500">*</span>}</label>
                          <input
                            type="email" name="email" value={formData.email} onChange={handleChange}
                            placeholder="staff@example.com"
                            className={`${FIELD_CLASS} ${emailCheck.state === 'taken' ? 'border-red-400 dark:border-red-500' : ''}`}
                          />
                          {emailCheck.state === 'checking' && (
                            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">Checking…</p>
                          )}
                          {emailCheck.state === 'taken' && (
                            <p className="text-[12px] font-semibold text-red-600 dark:text-red-400 mt-1.5">{emailCheck.message}</p>
                          )}
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Telephone</label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="e.g. +44 7700 900000" className={FIELD_CLASS} />
                        </div>

                        {/* No "User role" question any more. What a person can
                            actually do is set on the Access step: the Carer
                            toggle (mobile app) and the module ticks
                            (dashboard). The role id is derived from the toggle
                            by the API — bookkeeping, not something to ask a
                            manager about. */}
                      </div>

                      {/* No User ID or PIN fields: the id is generated on save
                          and the PIN is set by the staff member through an
                          emailed link, exactly as in Recruitment. */}
                      {!isEdit && (
                        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/40">
                          <Mail className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                          <p className="text-[13px] text-[#475569] dark:text-gray-400 leading-relaxed">
                            Their <strong className="text-[#1E293B] dark:text-gray-200">User ID is created automatically</strong> when you save.
                            We'll email them a link to set their own login PIN — nobody else ever sets it for them.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 2. Personal & identity */}
                  {subStep === 2 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 2 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Personal &amp; identity</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Personal and equality-monitoring information. Right to Work and identity evidence are recorded in Checks.</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                        <div>
                          <label className={LABEL_CLASS}>Date of birth</label>
                          <input type="date" name="dob" value={formData.userDetails.dob} onChange={handleDetailsChange} className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>National Insurance number</label>
                          <input type="text" name="ni_number" value={formData.userDetails.ni_number} onChange={handleDetailsChange} placeholder="e.g. QQ 12 34 56 C" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Nationality</label>
                          <input type="text" name="nationality" value={formData.userDetails.nationality} onChange={handleDetailsChange} placeholder="Nationality" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Gender <span className="text-[#94A3B8] font-normal">Optional equality monitoring</span></label>
                          <Select
                            value={formData.userDetails.gender}
                            onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, gender: v } }))}
                            options={[
                              { value: 'Prefer not to say', label: 'Prefer not to say' },
                              { value: 'Female', label: 'Female' },
                              { value: 'Male', label: 'Male' },
                              { value: 'Non-binary', label: 'Non-binary' },
                              { value: 'Self-describe', label: 'Self-describe' },
                            ]}
                            placeholder="Prefer not to say"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Ethnicity <span className="text-[#94A3B8] font-normal">Optional equality monitoring</span></label>
                          <input type="text" name="ethnicity" value={formData.userDetails.ethnicity} onChange={handleDetailsChange} placeholder="Optional" className={FIELD_CLASS} />
                        </div>
                        <div className="md:col-span-3">
                          <label className={LABEL_CLASS}>Disability or reasonable adjustments <span className="text-[#94A3B8] font-normal">Only information needed to support the worker at work</span></label>
                          <textarea name="disability_status" value={formData.userDetails.disability_status} onChange={handleDetailsChange} rows="3" placeholder="Optional" className={`${FIELD_CLASS} resize-none`} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3. Address */}
                  {subStep === 3 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 3 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Address</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">The worker can complete this securely after their record is created.</p>
                      </div>

                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">Current address</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                        <div className="md:col-span-2">
                          <label className={LABEL_CLASS}>Address line 1</label>
                          <input type="text" name="address_line1" value={formData.userDetails.address_line1} onChange={handleDetailsChange} placeholder="House number and street" className={FIELD_CLASS} />
                        </div>
                        <div className="md:col-span-2">
                          <label className={LABEL_CLASS}>Address line 2 <span className="text-[#94A3B8] font-normal">Optional</span></label>
                          <input type="text" name="address_line2" value={formData.userDetails.address_line2} onChange={handleDetailsChange} placeholder="Flat, building or area" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Town or city</label>
                          <input type="text" name="address_town" value={formData.userDetails.address_town} onChange={handleDetailsChange} placeholder="Town or city" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Postcode</label>
                          <input type="text" name="address_postcode" value={formData.userDetails.address_postcode} onChange={handleDetailsChange} placeholder="e.g. SW1A 1AA" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Country</label>
                          <input type="text" name="address_country" value={formData.userDetails.address_country} onChange={handleDetailsChange} placeholder="e.g. United Kingdom" className={FIELD_CLASS} />
                        </div>
                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2 text-[13px] font-semibold text-[#475569] dark:text-gray-300">
                            <input type="checkbox" name="address_under_3_years" checked={!!formData.userDetails.address_under_3_years} onChange={handleDetailsChange} className="w-4 h-4 rounded border-gray-300 text-[#2563EB]" />
                            I have lived at this address for less than 3 years
                          </label>
                        </div>
                      </div>

                      {/* Address history is only asked for when it is actually
                          needed, which is what the tickbox above decides. */}
                      {formData.userDetails.address_under_3_years && (
                        <div className="space-y-4 pt-2">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">Previous address</p>
                          <div>
                            <label className={LABEL_CLASS}>Previous address</label>
                            <textarea name="previous_addresses_text" value={formData.userDetails.previous_addresses_text} onChange={handleDetailsChange} rows="3" placeholder="House number, street, town or city, postcode and country" className={`${FIELD_CLASS} resize-none`} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 4. Emergency contact & next of kin */}
                  {subStep === 4 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 4 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Emergency contact &amp; next of kin</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Add the person to contact in an emergency. They can be the next of kin too.</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                        <div>
                          <label className={LABEL_CLASS}>Emergency contact name</label>
                          <input type="text" name="emergency_contact_name" value={formData.userDetails.emergency_contact_name} onChange={handleDetailsChange} placeholder="Contact name" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Relationship</label>
                          <input type="text" name="emergency_contact_rel" value={formData.userDetails.emergency_contact_rel} onChange={handleDetailsChange} placeholder="e.g. Partner, parent" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Telephone</label>
                          <input type="tel" name="emergency_contact_phone" value={formData.userDetails.emergency_contact_phone} onChange={handleDetailsChange} placeholder="Telephone" className={FIELD_CLASS} />
                        </div>
                        <div className="md:col-span-3">
                          <label className="flex items-center gap-2 text-[13px] font-semibold text-[#475569] dark:text-gray-300">
                            <input type="checkbox" name="kin_same_as_emergency" checked={!!formData.userDetails.kin_same_as_emergency} onChange={handleDetailsChange} className="w-4 h-4 rounded border-gray-300 text-[#2563EB]" />
                            This emergency contact is also the worker's next of kin
                          </label>
                        </div>
                      </div>

                      {!formData.userDetails.kin_same_as_emergency && (
                        <div className="space-y-4 pt-2">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">Next of kin</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                            <div>
                              <label className={LABEL_CLASS}>Full name</label>
                              <input type="text" value={formData.userDetails.next_of_kin?.name || ''} onChange={(e) => handleNextOfKinChange('name', e.target.value)} placeholder="Next-of-kin name" className={FIELD_CLASS} />
                            </div>
                            <div>
                              <label className={LABEL_CLASS}>Relationship</label>
                              <input type="text" value={formData.userDetails.next_of_kin?.relationship || ''} onChange={(e) => handleNextOfKinChange('relationship', e.target.value)} placeholder="e.g. Parent, sibling" className={FIELD_CLASS} />
                            </div>
                            <div>
                              <label className={LABEL_CLASS}>Telephone</label>
                              <input type="tel" value={formData.userDetails.next_of_kin?.phone || ''} onChange={(e) => handleNextOfKinChange('phone', e.target.value)} placeholder="Telephone" className={FIELD_CLASS} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 5. Bank details */}
                  {subStep === 5 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 5 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Bank details</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Payroll information. The worker can complete this securely; only authorised payroll users can view it.</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                        <div className="md:col-span-3">
                          <label className={LABEL_CLASS}>Account holder name</label>
                          <input type="text" name="bank_account_name" value={formData.userDetails.bank_account_name} onChange={handleDetailsChange} placeholder="Name on the bank account" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Sort code</label>
                          <input type="text" inputMode="numeric" maxLength={8} value={formData.userDetails.bank_sort_code} onChange={handleSortCodeChange} placeholder="00-00-00" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Account number</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={8}
                            value={formData.userDetails.bank_account_number}
                            onChange={(e) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, bank_account_number: e.target.value.replace(/\D/g, '').slice(0, 8) } }))}
                            placeholder="8 digits"
                            className={FIELD_CLASS}
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Confirm account number</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={8}
                            value={confirmAccountNumber}
                            onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 8))}
                            placeholder="Type account number again"
                            className={`${FIELD_CLASS} ${accountNumbersMatch ? '' : 'border-rose-400'}`}
                          />
                          {!accountNumbersMatch && (
                            <p className="text-[12px] text-rose-600 mt-1.5">The account numbers do not match.</p>
                          )}
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Building society roll number <span className="text-[#94A3B8] font-normal">Optional</span></label>
                          <input type="text" name="bank_building_society_ref" value={formData.userDetails.bank_building_society_ref} onChange={handleDetailsChange} placeholder="Only for a building society account" className={FIELD_CLASS} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 6. Employment details */}
                  {subStep === 6 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 6 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Employment details</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Only the employment essentials. Exact days and times are set in Work Roster next.</p>
                      </div>

                      {/* Hourly rate, max visits per day and skills are not
                          asked for here — they are not in the prototype's Basic
                          details. Their columns and any existing values are
                          untouched: an edit round-trips them unchanged, they
                          are simply no longer set from this screen. */}

                      {/* Job title is the only job field. There was a "Job role"
                          dropdown beside it (Care, Clinical / nursing, …) saying
                          much the same thing in coarser terms; it is gone, and
                          its column keeps whatever it already held. */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                        <div>
                          <label className={LABEL_CLASS}>Job title <span className="text-red-500">*</span></label>
                          {/* A list, but not a closed one — a home with a job
                              title of its own can still type it in. */}
                          <input type="text" name="job_title" list="staffJobTitleOptions" value={formData.userDetails.job_title} onChange={handleDetailsChange} placeholder="Type or choose a job title" className={FIELD_CLASS} />
                          <datalist id="staffJobTitleOptions">
                            {JOB_TITLES.map(title => <option key={title} value={title} />)}
                          </datalist>
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Employment type</label>
                          <Select
                            value={formData.userDetails.employment_type}
                            onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, employment_type: v } }))}
                            options={[
                              { value: 'Full-time', label: 'Full-time' },
                              { value: 'Part-time', label: 'Part-time' },
                              // A record created before this screen may hold
                              // Bank, Agency or Volunteer. Offering it back stops
                              // an unrelated edit silently changing someone's
                              // employment type.
                              ...(!formData.userDetails.employment_type || ['Full-time', 'Part-time'].includes(formData.userDetails.employment_type)
                                ? []
                                : [{ value: formData.userDetails.employment_type, label: formData.userDetails.employment_type }]),
                            ]}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Contract type</label>
                          <Select
                            value={formData.userDetails.contract_type}
                            onChange={handleContractTypeChange}
                            options={[
                              ...CONTRACT_TYPES,
                              ...(!formData.userDetails.contract_type || CONTRACT_TYPES.some(type => type.value === formData.userDetails.contract_type)
                                ? []
                                : [{ value: formData.userDetails.contract_type, label: formData.userDetails.contract_type }]),
                            ]}
                            className="w-full"
                          />
                        </div>
                        {formData.userDetails.contract_type === 'Fixed-term' && (
                          <div>
                            <label className={LABEL_CLASS}>Contract end date</label>
                            <input type="date" name="contract_end_date" value={formData.userDetails.contract_end_date || ''} onChange={handleDetailsChange} className={FIELD_CLASS} />
                          </div>
                        )}
                        <div>
                          <label className={LABEL_CLASS}>Start date <span className="text-red-500">*</span></label>
                          <input type="date" name="start_date" value={formData.userDetails.start_date} onChange={handleDetailsChange} className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Notice period</label>
                          <input type="text" name="notice_period" value={formData.userDetails.notice_period} onChange={handleDetailsChange} placeholder="e.g. 4 weeks" className={FIELD_CLASS} />
                        </div>
                      </div>

                      {/* Employment history — where they have worked BEFORE
                          this job, kept apart from the post above so the two
                          are never confused. Optional: a first job has none. */}
                      <div className="pt-6 border-t border-[#E2E8F0] dark:border-gray-800">
                        <h4 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">Employment history</h4>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1 mb-4">
                          Previous roles and any gaps — separate from the job being added above.
                        </p>
                        <EmploymentHistory
                          value={formData.userDetails.previous_employers}
                          onChange={(next) => setFormData(prev => ({
                            ...prev,
                            userDetails: { ...prev.userDetails, previous_employers: next },
                          }))}
                        />
                      </div>
                    </div>
                  )}

                  {/* 7. Checks & compliance */}
                  {subStep === 7 && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 7 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Checks &amp; compliance</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Complete Right to Work first, then DBS. These details use the same fields as Staff Compliance.</p>
                      </div>

                      {/* Right to Work */}
                      <div className="space-y-6">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">1. Right to Work</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                          <div>
                            <label className={LABEL_CLASS}>Status</label>
                            <Select
                              value={formData.userDetails.rtw_status}
                              onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, rtw_status: v } }))}
                              options={CHECK_STATUSES.map(status => ({ value: status, label: status }))}
                              placeholder="Choose status"
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Document type</label>
                            <input type="text" name="rtw_doc_type" value={formData.userDetails.rtw_doc_type} onChange={handleDetailsChange} placeholder="e.g. British passport / Share code" className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Reference number</label>
                            <input type="text" name="rtw_doc_number" value={formData.userDetails.rtw_doc_number} onChange={handleDetailsChange} placeholder="Document or share-code reference" className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Check date</label>
                            <input type="date" name="rtw_verification_date" value={formData.userDetails.rtw_verification_date} onChange={handleDetailsChange} className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Expiry / recheck due</label>
                            <input type="date" name="rtw_expiry_date" value={formData.userDetails.rtw_expiry_date} onChange={handleDetailsChange} className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Reminder schedule</label>
                            <Select
                              value={formData.userDetails.rtw_reminder}
                              onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, rtw_reminder: v } }))}
                              options={REMINDER_SCHEDULES}
                              className="w-full"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className={LABEL_CLASS}>Proof attachment <span className="text-[#94A3B8] font-normal">Optional</span></label>
                            <input
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) queueOrUploadDocument({ category: 'Right to Work evidence', name: `Right to Work — ${file.name}`, expiry: formData.userDetails.rtw_expiry_date, file });
                              }}
                              disabled={uploadingDocument}
                              className={`${FIELD_CLASS} file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[13px] file:font-semibold file:bg-blue-50 file:text-[#2563EB]`}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className={LABEL_CLASS}>Manager notes <span className="text-[#94A3B8] font-normal">Optional</span></label>
                            <textarea name="rtw_notes" value={formData.userDetails.rtw_notes} onChange={handleDetailsChange} rows="3" placeholder="Record how the check was completed, any restrictions or follow-up" className={`${FIELD_CLASS} resize-none`} />
                          </div>
                        </div>
                      </div>

                      {/* DBS */}
                      <div className="space-y-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">2. DBS check</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                          <div>
                            <label className={LABEL_CLASS}>Status</label>
                            <Select
                              value={formData.userDetails.dbs_status}
                              onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, dbs_status: v } }))}
                              options={CHECK_STATUSES.map(status => ({ value: status, label: status }))}
                              placeholder="Choose status"
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>DBS type</label>
                            <input type="text" name="dbs_level" value={formData.userDetails.dbs_level} onChange={handleDetailsChange} placeholder="e.g. Enhanced DBS, adult workforce" className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Certificate number</label>
                            <input type="text" name="dbs_cert_number" value={formData.userDetails.dbs_cert_number} onChange={handleDetailsChange} placeholder="Certificate number" className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Issue date</label>
                            <input type="date" name="dbs_issue_date" value={formData.userDetails.dbs_issue_date} onChange={handleDetailsChange} className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Expiry / recheck due</label>
                            <input type="date" name="dbs_expiry" value={formData.userDetails.dbs_expiry} onChange={handleDetailsChange} className={FIELD_CLASS} />
                          </div>
                          <div>
                            <label className={LABEL_CLASS}>Reminder schedule</label>
                            <Select
                              value={formData.userDetails.dbs_reminder}
                              onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, dbs_reminder: v } }))}
                              options={REMINDER_SCHEDULES}
                              className="w-full"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className={LABEL_CLASS}>Proof attachment <span className="text-[#94A3B8] font-normal">Optional</span></label>
                            <input
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) queueOrUploadDocument({ category: 'DBS certificate / result', name: `DBS — ${file.name}`, expiry: formData.userDetails.dbs_expiry, file });
                              }}
                              disabled={uploadingDocument}
                              className={`${FIELD_CLASS} file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[13px] file:font-semibold file:bg-blue-50 file:text-[#2563EB]`}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className={LABEL_CLASS}>Manager notes <span className="text-[#94A3B8] font-normal">Optional</span></label>
                            <textarea name="dbs_notes" value={formData.userDetails.dbs_notes} onChange={handleDetailsChange} rows="3" placeholder="Record the suitability decision or notes" className={`${FIELD_CLASS} resize-none`} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 8. Documents */}
                  {subStep === 8 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563EB] mb-1">Section 8 of {BASIC_SECTIONS.length}</p>
                        <h3 className="text-[18px] font-semibold text-[#1E293B] dark:text-gray-200">Documents</h3>
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">Store staff documents that are separate from Right to Work and DBS evidence.</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                        <div>
                          <label className={LABEL_CLASS}>Document category</label>
                          <Select
                            value={documentDraft.category}
                            onChange={(v) => setDocumentDraft(prev => ({ ...prev, category: v }))}
                            options={DOCUMENT_CATEGORIES.map(category => ({ value: category, label: category }))}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Document name</label>
                          <input type="text" value={documentDraft.name} onChange={(e) => setDocumentDraft(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Care Certificate" className={FIELD_CLASS} />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Review / expiry date <span className="text-[#94A3B8] font-normal">Optional</span></label>
                          <input type="date" value={documentDraft.expiry} onChange={(e) => setDocumentDraft(prev => ({ ...prev, expiry: e.target.value }))} className={FIELD_CLASS} />
                        </div>
                        <div className="md:col-span-3">
                          <label className={LABEL_CLASS}>Upload document</label>
                          <input
                            type="file"
                            onChange={(e) => setDocumentDraft(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                            className={`${FIELD_CLASS} file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[13px] file:font-semibold file:bg-blue-50 file:text-[#2563EB]`}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="text-[12px] text-[#94A3B8] dark:text-gray-500">
                          {isEdit
                            ? 'Documents are saved as soon as you add them.'
                            : 'Documents are uploaded once the staff member has been created.'}
                        </span>
                        <Button type="button" variant="primary" onClick={addDraftDocument} loading={uploadingDocument} disabled={!documentDraft.file}>
                          Add document
                        </Button>
                      </div>

                      {/* Already saved against this staff member, plus anything
                          still waiting for the record to exist. */}
                      {(staffDocuments.length > 0 || pendingDocuments.length > 0) && (
                        <div className="space-y-2">
                          {staffDocuments.map((document) => (
                            <div key={document.id} className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]">
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">{document.name}</p>
                                <p className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">
                                  {document.category}
                                  {document.expiry ? ` · review ${document.expiry}` : ''}
                                  {document.file_name ? ` · ${document.file_name}` : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                {document.file_url && (
                                  <a href={document.file_url} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-[#2563EB] hover:underline">View</a>
                                )}
                                <button type="button" onClick={() => removeSavedDocument(document.id)} className="text-[12px] font-semibold text-rose-600 hover:underline">Remove</button>
                              </div>
                            </div>
                          ))}
                          {pendingDocuments.map((document, index) => (
                            <div key={`pending-${index}`} className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-dashed border-amber-300 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/10">
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">{document.name}</p>
                                <p className="text-[12px] text-amber-700 dark:text-amber-500 truncate">
                                  {document.category} · uploads when you save
                                </p>
                              </div>
                              <button type="button" onClick={() => setPendingDocuments(prev => prev.filter((_, i) => i !== index))} className="text-[12px] font-semibold text-rose-600 hover:underline shrink-0">Remove</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Work roster & payroll
                  A care home rosters SHIFTS (working, start, finish, unpaid
                  break, and the paid hours that follow). A domiciliary service
                  records AVAILABILITY (available, from, until) — no break and
                  no paid hours, because the visits themselves are assigned
                  later in Rota. Same seven rows, read to suit the service. */}
              {currentStep === 2 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-300">

                  <div className="flex items-start justify-between gap-4 mb-6">
                    <p className="text-[13px] text-[#64748B] dark:text-gray-400 max-w-2xl">
                      {isDomiciliaryOrg
                        ? 'Set the days and hours they are normally available. Turn a day off when they are not available at all.'
                        : 'Set the usual weekly pattern. Turn an individual day off when they do not normally work it.'}
                    </p>
                    {/* Not a switch: which grid you get follows the
                        organisation's own type, so a care home can never store
                        availability and a domiciliary service can never store
                        shifts it does not roster. */}
                    <span className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F1F5F9] dark:bg-[#31373f] text-[12px] font-semibold text-[#475569] dark:text-gray-300">
                      {isDomiciliaryOrg ? 'Domiciliary care' : 'Care home'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6 mb-6">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400">Team</label>
                        <button
                          type="button"
                          onClick={() => setIsCreatingGroup(!isCreatingGroup)}
                          className="text-[12px] font-bold text-[#2563EB] hover:underline"
                        >
                          {isCreatingGroup ? 'Cancel' : '+ Create a team'}
                        </button>
                      </div>

                      {isCreatingGroup ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="e.g. Dementia Care Team"
                            className="flex-1 px-4 py-2 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-xl text-[14px]"
                          />
                          <button
                            type="button"
                            onClick={handleCreateGroup}
                            className="bg-[#2563EB] text-white px-4 rounded-xl text-[13px] font-bold hover:bg-blue-700 transition"
                          >
                            Add
                          </button>
                        </div>
                      ) : (
                        <Select
                          value={formData.group_id}
                          onChange={(v) => setFormData(prev => ({ ...prev, group_id: v }))}
                          options={groups.map(g => ({ value: String(g.id), label: g.name }))}
                          placeholder="Choose a team"
                          className="w-full"
                        />
                      )}
                    </div>

                    <div>
                      <label className={LABEL_CLASS}>Contracted weekly hours</label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.contracted_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, contracted_hours: e.target.value }))}
                        placeholder="e.g. 37.5"
                        className={FIELD_CLASS}
                      />
                    </div>

                    {!isDomiciliaryOrg && (
                      <div>
                        <label className={LABEL_CLASS}>Shift name</label>
                        <Select
                          value={formData.shift_name}
                          onChange={(v) => setFormData(prev => ({ ...prev, shift_name: v }))}
                          options={[
                            { value: 'Day', label: 'Day' },
                            { value: 'Night', label: 'Night' },
                            { value: 'Evening', label: 'Evening' },
                          ]}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>

                  <WeekPatternGrid
                    value={formData.week_pattern}
                    onChange={(week_pattern) => setFormData((prev) => ({ ...prev, week_pattern }))}
                    domiciliary={isDomiciliaryOrg}
                  />

                  {isDomiciliaryOrg && (
                    <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-3">
                      Availability is not a shift commitment — visits are assigned later in Rota.
                    </p>
                  )}

                  {/* PAYROLL */}
                  <p className="text-[11px] font-bold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wider mt-8 mb-4">Payroll</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                    <div>
                      <label className={LABEL_CLASS}>Hourly pay rate</label>
                      {/* The ONE rate every pay calculation uses. Stored on the
                          employment record and read live by the pay run —
                          nothing is stamped onto a shift or a visit, so
                          correcting it here fixes future pay runs without
                          rewriting history. See api/utils/payrollService.js. */}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="hourly_rate"
                        value={formData.userDetails.hourly_rate}
                        onChange={handleDetailsChange}
                        placeholder="e.g. 12.50"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Pay frequency</label>
                      <Select
                        value={formData.userDetails.pay_frequency}
                        onChange={(v) => setFormData(prev => ({ ...prev, userDetails: { ...prev.userDetails, pay_frequency: v } }))}
                        options={PAY_FREQUENCIES.map(f => ({ value: f, label: f }))}
                        placeholder="Monthly"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>
                        Payroll reference <span className="text-[#94A3B8] dark:text-gray-500 font-normal">Optional</span>
                      </label>
                      <input
                        type="text"
                        name="payroll_reference"
                        value={formData.userDetails.payroll_reference}
                        onChange={handleDetailsChange}
                        placeholder="Set by payroll"
                        className={FIELD_CLASS}
                      />
                    </div>
                  </div>

                  {/* The weekly total, against the contract where one was given.
                      Availability has no paid hours to total. */}
                  {!isDomiciliaryOrg && (
                    <div className="flex items-center justify-between gap-4 mt-6 px-5 py-4 rounded-xl bg-[#F8FAFC] dark:bg-[#20242c] border border-[#E2E8F0] dark:border-gray-800">
                      <div>
                        <p className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">Total paid hours this week</p>
                        <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">
                          {!hasContractedHours
                            ? 'Enter contracted hours to compare'
                            : hoursDifference === 0
                              ? `Matches the ${contractedHoursNumber} contracted hours`
                              : hoursDifference > 0
                                ? `${hoursDifference} h above the ${contractedHoursNumber} contracted`
                                : `${Math.abs(hoursDifference)} h below the ${contractedHoursNumber} contracted`}
                        </p>
                      </div>
                      <span className={`text-[20px] font-black shrink-0 ${
                        hasContractedHours && hoursDifference !== 0
                          ? 'text-amber-600 dark:text-amber-500'
                          : 'text-[#2563EB]'
                      }`}>
                        {paidHoursThisWeek} hours
                      </span>
                    </div>
                  )}

                  <div className="mt-3 px-5 py-3 rounded-xl bg-[#EFF6FF] dark:bg-blue-900/10 border border-[#DBEAFE] dark:border-blue-900/40">
                    <p className="text-[12.5px] text-[#1D4ED8] dark:text-blue-400 font-medium">
                      {isDomiciliaryOrg
                        ? `${workingDayCount} available ${workingDayCount === 1 ? 'day' : 'days'} per week.`
                        : `${workingDayCount} working ${workingDayCount === 1 ? 'day' : 'days'} · ${paidHoursThisWeek} scheduled hours per week.`}
                    </p>
                  </div>
                </div>
              )}


              {/* STEP 3: Access — mobile app (the Carer toggle) and dashboard
                  (the module ticks). Two independent axes: either, or both. */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">

                  <StaffAccessPicker
                    isCarer={formData.is_carer}
                    appAccess={formData.app_access}
                    medicationAccess={formData.medication_access}
                    menuIds={formData.menu_ids}
                    availableMenus={availableMenus}
                    loadingMenus={loadingMenus}
                    onChange={(changes) => setFormData((prev) => ({ ...prev, ...changes }))}
                    canMakeManager={isOwnerRole(user)}
                    isManager={formData.is_manager}
                    sites={sites}
                    siteIds={managerSiteIdsOf(formData)}
                  />
                </div>
              )}

              {/* Form Actions Footer */}
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E2E8F0] dark:border-gray-800">
                {/* Left Side: Cancel or Back */}
                {currentStep === 1 && subStep === 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/manager/staffs')}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handlePrev}
                  >
                    Back
                  </Button>
                )}

                {/* Right Side: Save / Next, or Submit.
                    While EDITING these are two buttons, not one: Save changes
                    writes and stays put, Next moves on (saving first if the
                    section is dirty, so no edit is dropped on the way past).
                    One "Save & Next" button meant there was no way to save
                    without leaving the section you were working in.

                    The unsaved marker is the answer to "did that save?" while
                    you are still on the section; the toast answers it at the
                    moment of saving. Creating somebody still walks to the end,
                    where their login id and set-PIN email are produced from one
                    save, so there is nothing to save early. */}
                {currentStep < totalSteps ? (
                  <div className="flex items-center gap-3">
                    {isEdit && dirty && (
                      <span className="hidden sm:flex items-center gap-1.5 text-[12.5px] text-[#B45309] dark:text-amber-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                        Unsaved changes
                      </span>
                    )}
                    {isEdit && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleSaveOnly}
                        loading={submitting}
                        disabled={!dirty}
                      >
                        Save changes
                      </Button>
                    )}
                    {/* Disabled rather than spinning while a save is in
                        flight: Next saves a dirty section first, and two
                        spinners for one write reads as two writes. */}
                    <Button
                      type="button"
                      variant="primary"
                      onClick={isEdit ? handleNextWhileEditing : handleNext}
                      disabled={submitting}
                    >
                      {isEdit ? 'Next →' : 'Next Step'}
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSubmit}
                    loading={submitting}
                  >
                    {isApprovalMode
                      ? (submitting ? 'Approving...' : 'Approve & Save Profile')
                      : (isEdit ? 'Save Changes' : 'Create Staff Member')}
                  </Button>
                )}
              </div>
            </form>

          </div>
        </div>

      </div>
    </div>
  );
};

export default CreateEditStaff;